import { Component, ViewChild } from '@angular/core';
import { Globals } from '../../services/globals';
import { Child } from '../../models/child';
import { Packet } from '../../models/packet';
import { Calendar } from '../../components/appointment-calendar/calendar';
import { Anniversary } from '../../components/appointment-calendar/anniversary';
import { AppointmentService } from '../../services/appointment.service';
import { PacketService } from '../../services/packet.service';

/**
 * 기념일 타입 열거체
 * @enum
 */
enum ANNIV_TYPE {
    ETC,
    HOLY,
    EVENT,
    PAYMENT = 5,
    ATTENDANCE = 8,
    APPOINTMENT = 9,
}

@Component({
  selector: 'appointment',
  templateUrl: './appointment.html',
  styleUrls: ['./appointment.scss'],
})

export class AppointmentPage {

  /**
   * 기념일 타입에 매핑되는 타입 이름 및 설명
   * 각 index는 ANNIV_TYPE과 일치
   */
  private static ANNIV_TYPES: any = {
    0: { type: 'etc', desc: '기타' },
    1: { type: 'holy', desc: '휴무' },
    2: { type: 'event', desc: '이벤트' },
    5: { type: 'payment', desc: '결제' },
    8: { type: 'attendance', desc: '출석' },
    9: { type: 'apmt', desc: '예약' },
  };

  private $currentDate: Date = new Date();

  /**
   * 달력 컴포넌트
   */
  @ViewChild(Calendar) private calendar: Calendar;

  /**
   * 기념일 목록
   */
  anniversaries: Anniversary[] = [];

  /**
   * 현재 아동 정보
   */
  child: Child = new Child();

  /**
   * 패킷 정보
   */
  packet: Packet;

  /**
   * 선택한 날짜에 이벤트 존재 여부
   */
  isExistDayEvent: boolean = false;

  /**
   * 선택한 날짜의 예약 시간
   */
  dayAppointmentTime: Date;

  /**
   * 선택한 날짜의 패킷 정보
   */
  dayPacket: Packet;

  /**
   * 결제예정일 정보
   */
  dayPayment: Date;

  /**
   * 선택한 날짜의 이벤트 정보
   */
  dayEvents: string[] = [];

  /**
   * 선택한 날짜의 휴무 정보
   */
  holydayDesc: string = '';

  /**
   * 선택한 날짜의 출석일 여부
   */
  isAttendanceDay: boolean = false;

  constructor(
    private $appointmentService: AppointmentService,
    private $packetService: PacketService,
    private $g: Globals) {
    this.viewEnter();
  }

  /**
   * 정보 업데이트
   */
  viewEnter() {
    this.requestAnniversaries(this.$currentDate);
    this.requestPacket();
    this.child = this.$g.child;
  }

  /**
   * 달력 날짜 선택 시 호출되는 이벤트 함수 (emit)
   * @param {{date: Date, anniversaries: Array<Anniversary>}} obj 선택한 날짜 객체
   */
  onDaySelected(obj: any) {
    let isExistDayEvent = false;
    const annivs = obj.anniversaries as Anniversary;
    let isAppointmentDay = false;
    let paymentDay = null;
    const events: string[] = [];
    let dayAppointmentTime = null;
    let isAttendanceDay = false;
    let holydayDesc = '';

    // 선택한 날짜의 기념일 순회
    for (let i = 0, d: Anniversary; d = annivs[i]; i += 1) {
      switch (d.type) {
        // 예약일 이라면 예약날 여부와 예약 날짜 저장
        case AppointmentPage.ANNIV_TYPES[ANNIV_TYPE.APPOINTMENT].type:
          dayAppointmentTime = new Date(d.desc);
          isAppointmentDay = true;
          isExistDayEvent = true;
          break;
        // 이벤트라면 이벤트 설명 저장
        case AppointmentPage.ANNIV_TYPES[ANNIV_TYPE.EVENT].type:
          events.push(d.desc);
          isExistDayEvent = true;
          break;
        // 결졔에정일 이라면 날짜 저장
        case AppointmentPage.ANNIV_TYPES[ANNIV_TYPE.PAYMENT].type:
          paymentDay = d.date;
          isExistDayEvent = true;
          break;
        // 출석일 이라면 출석날 여부 저장
        case AppointmentPage.ANNIV_TYPES[ANNIV_TYPE.ATTENDANCE].type:
          isAttendanceDay = true;
          isExistDayEvent = true;
          break;
        case AppointmentPage.ANNIV_TYPES[ANNIV_TYPE.HOLY].type:
          holydayDesc = d.desc;
          isExistDayEvent = true;
          break;
      }
    }

    // 로직 중 렌더링 되는 것을 막기 위해서 한 번에 저장
    // 예약날인 경우 패킷 정보 출력
    this.dayPacket = isAppointmentDay ? this.packet : null;
    this.dayAppointmentTime = dayAppointmentTime;
    // 이벤트 저장
    this.dayEvents = events;
    // 결제일 저장
    this.dayPayment = paymentDay;
    // 출석일 여부 저장
    this.isAttendanceDay = isAttendanceDay;
    // 기념일 여부
    this.isExistDayEvent = isExistDayEvent;
    // 휴무 저장
    this.holydayDesc = holydayDesc;
  }

  /**
   * 달력 달 변경 시 호출되는 이벤트 함수 (emit)
   * @param {Date} date 달력 날짜
   */
  onMonthChanged(date: Date) {
    this.$currentDate = date;
    this.dayPacket = null;
    this.dayAppointmentTime = null;
    this.dayEvents = [];
    this.dayPayment = null;
    this.isAttendanceDay = false;
    this.isExistDayEvent = false;
    this.holydayDesc = null;
    this.requestAnniversaries(date);
  }

  /**
   * 서버로부터 예약, 이벤트 정보를 받아서 저장
   * @param {Date} date 기준 날짜
   */
  private requestAnniversaries(date) {
    // 현재 아동 식별 번호
    const bmiNum = this.$g.child.num;
    // 기념일 목록
    let anniversaries: Anniversary[] = [];
    // 현재 컴포넌트

    // 센터 이벤트 정보
    const p1 = this.$appointmentService.centerEvent(bmiNum).toPromise().then(
      (res) => {
        const data = res && res.data;
        if (data) {
          const annivs = data.map((item) => {
            const date = new Date(item.holi_start);
            const name = AppointmentPage.ANNIV_TYPES[item.holi_type].desc;
            const type = AppointmentPage.ANNIV_TYPES[item.holi_type].type;
            const desc = item.holi_title;
            return new Anniversary(date, name, type, desc);
          });
          anniversaries = anniversaries.concat(annivs);
        }
      },
      () => {});

    // 아동 예약 정보
    // 기념일 설명에 예약 시간 저장
    const p2 = this.$appointmentService.appointment(bmiNum, date).toPromise().then(
      (res) => {
        const data = res && res.data;
        const dataPayment = res && res.dataPayment;
        if (data) {
          // 예약 또는 출석 정보
          const annivs = data.map((item) => {
            const annivType = item.res_attend ?
            ANNIV_TYPE.ATTENDANCE : ANNIV_TYPE.APPOINTMENT; // 출석 정보 여부, 출석 정보라면 1이 올 것
            const date = new Date(item.res_start_time);
            const name = AppointmentPage.ANNIV_TYPES[annivType].desc;
            const type = AppointmentPage.ANNIV_TYPES[annivType].type;
            const desc = item.res_start_time;
            return new Anniversary(date, name, type, desc);
          });
          anniversaries = anniversaries.concat(annivs);
        }

        // 결제 예정일
        if (dataPayment) {
          const payment = new Anniversary(
              new Date(dataPayment.payment),
              AppointmentPage.ANNIV_TYPES[ANNIV_TYPE.PAYMENT].desc,
              AppointmentPage.ANNIV_TYPES[ANNIV_TYPE.PAYMENT].type);
          anniversaries.push(payment);
        }
      },
      () => {});

    // 이벤트, 예약 정보를 얻은 후 달력 갱신
    Promise.all([p1, p2]).then(() => {
      this.anniversaries = anniversaries;
      setTimeout(
        () => {
          this.calendar.selectToday();
        },
        0);
    });
  }

  /**
   * 서버로부터 패킷 정보를 획득하여 출력
   */
  private requestPacket() {
    const bmiNum = this.$g.child.num;
    this.$packetService.packet(bmiNum).subscribe(
      (res) => {
        const data = res && res.data;
        if (data) {
          this.packet = new Packet(data);
        }
      },
      () => {
        this.packet = null;
      });
  }
}
